<template>
	<FormItem v-bind="validateInfos.ant">
		<template #label>
			<Tooltip placement="topLeft" color="black">
				<template #title>{{ $t('base-stations-edit.tooltips.ant') }}</template>
				<QuestionCircleOutlined /> {{ $t('base-stations-edit.fields.ant.label') }}
			</Tooltip>
		</template>
		<div class="antenna-wrapper">
			<AutoComplete
				class="antenna-input-field"
				v-model:value="antAutoCompleteValue"
				:options="suggestedAntennaOptions"
				:filterOption="false"
			>
				<template #option="{ value: val }">
					<span class="show-whitespace">{{ val }}</span>
				</template>
				<Input :maxlength="ANT_MAX_LENGTH" showCount />
			</AutoComplete>
			<div v-if="antWarningMessages.length" class="antenna-warning-wrapper">
				<ul class="antenna-warning">
					<li v-for="message in antWarningMessages" :key="message">
						{{ message }}
					</li>
				</ul>
			</div>
		</div>
	</FormItem>

	<FormItem v-if="baseStationId !== 0 && baseStationDetailsRef.antDetected && formState.ant != baseStationDetailsRef.antDetected" class="pd-md-vertical">
		<template #label>
			<Tooltip placement="topLeft" color="black">
				<template #title>{{ $t('base-stations-edit.fields.ant.conflict.overwriteTooltip') }}</template>
				<QuestionCircleOutlined /> {{ $t('base-stations-edit.fields.ant.conflict.formLabel') }}
			</Tooltip>
		</template>
		<span class="pd-sm-x show-whitespace">{{ baseStationDetailsRef.antDetected }}</span>
		<Button @click.prevent="onOverwriteAnt">
			<template #icon>
				<ArrowUpOutlined />
			</template>
			{{ $t('base-stations-edit.fields.ant.conflict.overwriteButtonLabel') }}
		</Button>
	</FormItem>

	<LabeledSwitch v-if="smallScreen" class="pd-md-vertical" v-model="allowAntDetection" :rightLabel="$t('base-stations-edit.fields.allowAntDetection')" />
	<FormItem
		v-else
		class="pd-md-vertical"
		:label="$t('base-stations-edit.fields.allowAntDetection')"
		v-bind="validateInfos.allowAntDetection"
		:label-col="{ span: 12, lg: { span: 8 } }"
		:wrapper-col="{ span: 12, lg: { span: 16 } }"
	>
		<LabeledSwitch v-model="allowAntDetection" />
	</FormItem>
</template>

<script>
import { toRef, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { FormItem, Tooltip, Button, AutoComplete, Input } from 'ant-design-vue';
import { ArrowUpOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';

import Validators from '@/api/Validators';
import Consts from '@/api/Consts';

import LabeledSwitch from '@/components/common/LabeledSwitch';

import { useSubformFieldProxies } from '@/components/addBaseStation/useSubformFieldProxies';


export default {
	components: {
		FormItem, Tooltip, Button, AutoComplete, Input,
		ArrowUpOutlined, QuestionCircleOutlined,
		LabeledSwitch,
	},
	emits: ['updateFormState'],
	props: ['formState', 'formSettings', 'baseStationId', 'baseStationDetails', 'validateInfos', 'smallScreen'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const { ant, allowAntDetection } = useSubformFieldProxies(props.formState, ['ant', 'allowAntDetection'], emit);
		const baseStationDetailsRef = toRef(props, 'baseStationDetails');
		const formSettingsRef = toRef(props, 'formSettings');
		const onOverwriteAnt = () => {
			ant.value = baseStationDetailsRef.value.antDetected;
		};

		const antUpperCaseValue = computed(() => (ant.value || '').toUpperCase());
		const antNormalized = computed(() => antUpperCaseValue.value.trim());
		const supportedAntennaModels = computed(() => (formSettingsRef.value.supportedAntennas || []).map(item => item.toUpperCase()));
		const supportedAntennaSet = computed(() => new Set(supportedAntennaModels.value));
		const suggestedAntennaModels = computed(() => {
			const prefix = antUpperCaseValue.value;
			if (prefix.length === 0) {
				return supportedAntennaModels.value.slice(0, 100);
			}
			return supportedAntennaModels.value.filter(item => item.startsWith(prefix)).slice(0, 100);
		});
		const suggestedAntennaOptions = computed(() => suggestedAntennaModels.value.map(item => ({
			value: item,
		})));
		const antAutoCompleteValue = computed({
			get: () => ant.value,
			set: value => {
				const validateResult = Validators.antennaChecker(value || '');
				if (validateResult.valid) {
					ant.value = validateResult.value;
				}
			},
		});
		const antWarningMessages = computed(() => {
			const value = antNormalized.value;
			if (value.length === 0) {
				return [];
			}
			const messages = [];
			if (value.length < Validators.ANT_MAX_LENGTH) {
				messages.push(t('base-stations-edit.fields.ant.warnings.short', { value: props.formState.ant }));
			}
			if (!supportedAntennaSet.value.has(value)) {
				messages.push(t('base-stations-edit.fields.ant.warnings.unsupported', { value: props.formState.ant }));
			}
			return messages;
		});
		return {
			ant,
			allowAntDetection,
			baseStationDetailsRef,
			onOverwriteAnt,
			antAutoCompleteValue,
			suggestedAntennaOptions,
			antWarningMessages,
			ANT_MAX_LENGTH: Validators.ANT_MAX_LENGTH,
			WARNING_LIGHT: Consts.COLORS.warningLight,
			WARNING_DARK: Consts.COLORS.warningDark,
		}
	}
}
</script>

<style scoped>
.antenna-wrapper {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.antenna-input-field {
	width: 100%;
}

.antenna-warning-wrapper {
	border: 1px solid v-bind(WARNING_DARK);
	border-radius: 6px;
	padding: 8px;
	border-color: v-bind(WARNING_DARK);
	background-color: v-bind(WARNING_LIGHT);
}

ul.antenna-warning {
	padding-inline-start: 12px;
	margin-block: 0;
	font-size: 12px;
	line-height: 16px;
	color: v-bind(WARNING_DARK);
}

.antenna-warning li {
	margin-block-end: 6px;
}
.antenna-warning li:last-child {
	margin-block-end: 0;
}
</style>
